import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "react-use";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { StackedLineChart } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Paper, Typography } from "@mui/material";
import { Feature, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import { omit } from "lodash";
import { FeatureIdentifier } from "mapbox-gl";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api";
import { DRAWING_CONSTANTS, DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import directSelectLeadIns from "fond/draw/modes/design/leadIns/directSelectLeadIns";
import drawLeadIns from "fond/draw/modes/design/leadIns/drawLeadIns";
import { isValidTree } from "fond/draw/modes/design/leadIns/helper";
import leadInDrawStyles from "fond/draw/modes/design/leadIns/leadInDrawStyles";
import simpleSelectLeadIns from "fond/draw/modes/design/leadIns/simpleSelectLeadIns";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { DeleteButton, LineStringButton, PolygonSelectButton, RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { EditMode, setEditMode } from "fond/project/redux";
import { addImport, removeImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ImportStatus } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import { LayerIds, sourceId } from "../helper";

interface LeadInDrawPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const config: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="polygonSelect" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLineString" title="Place a new Span" icon={<StackedLineChart />} />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const modes = {
  simple_select: simpleSelectLeadIns,
  direct_select: directSelectLeadIns,
  draw_line_string: drawLeadIns,
};
const sources: FeatureIdentifier[] = [{ source: sourceId, sourceLayer: LayerIds.leadIns }];

const LeadInDrawPanel: React.FC<LeadInDrawPanelProps> = ({ isReadOnly }) => {
  const dispatch = useAppDispatch();
  const { drawControl, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const { enqueueSnackbar } = useSnackbar();
  const versionId = useAppSelector((state) => state.project.versionId);
  const leadInsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: LayerIds.leadIns }));
  const demandLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: LayerIds.demand }));
  const ugPointsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: LayerIds.ugPoints }));
  const { data: leadInsData, isLoading: isLoadingLeadIns } = useGetFeaturesQuery({ layerId: leadInsLayer?.ID || "" }, { skip: !leadInsLayer?.ID });
  const { data: demandsData, isLoading: isLoadingDemands } = useGetFeaturesQuery({ layerId: demandLayer?.ID || "" }, { skip: !demandLayer?.ID });
  const { data: ugPointsData, isLoading: isLoadingUgPoints } = useGetFeaturesQuery(
    { layerId: ugPointsLayer?.ID || "" },
    { skip: !ugPointsLayer?.ID }
  );
  const [isSaving, setIsSaving] = useState(false);
  const isLoading = isLoadingLeadIns || isLoadingDemands || isLoadingUgPoints;
  // Watch the import state for this layer
  const importState = useAppSelector((state) => state.imports[versionId]?.[LayerIds.leadIns]);

  // The initial features are derived from the data layer for lead-ins which
  // are drawn as LineStrings.
  const initialFeatures: Feature<LineString>[] = useMemo(() => {
    const leadIns = (structuredClone(leadInsData) || []) as Feature<LineString>[];

    if (!isLoading) {
      // Clone and add layerId to each feature type
      const clonedDemands = (structuredClone(demandsData) || []) as Feature<Point>[];
      const clonedUgPoints = (structuredClone(ugPointsData) || []) as Feature<Point>[];

      // Add layerId property for validation
      clonedDemands.forEach((f) => {
        f.properties = f.properties || {};
        f.properties.layerId = LayerIds.demand;
      });

      clonedUgPoints.forEach((f) => {
        f.properties = f.properties || {};
        f.properties.layerId = LayerIds.ugPoints;
      });

      // Build spatial index for efficient validation
      const tree = rbush();
      tree.load([...clonedDemands, ...clonedUgPoints]);

      return leadIns.map((f) => {
        if (f.properties) {
          f.properties[`${DRAWING_CONSTANTS.Valid}`] = String(
            isValidTree(f, tree, {
              demands: { sourceId, sourceLayerId: LayerIds.demand },
              ugPoints: { sourceId, sourceLayerId: LayerIds.ugPoints },
            })
          );
        }
        return f;
      });
    }

    return [];
  }, [demandsData, isLoading, leadInsData, ugPointsData]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    dispatch(setEditMode(EditMode.none));
  });

  const handleBack = useCallback(() => {
    setDrawMode("no_feature");
    goBack();
  }, [setDrawMode, goBack]);

  // Monitor import completion and navigate away when done
  useEffect(() => {
    if (!importState || !isSaving) return;

    const isComplete = importState.status === ImportStatus.COMPLETE;
    const isError = importState.status === ImportStatus.ERROR;

    if (isComplete) {
      // Remove the import from state to close the progress popup
      dispatch(removeImport({ versionId, layerKey: LayerIds.leadIns }));
      // Import finished successfully, navigate away
      handleBack();
    } else if (isError) {
      enqueueSnackbar("Failed to save changes. Please try again.", { variant: "error" });
      // Remove the import from state so user can retry
      dispatch(removeImport({ versionId, layerKey: LayerIds.leadIns }));
      // Stop loading state
      setIsSaving(false);
    }
  }, [handleBack, importState, isSaving, dispatch, versionId, enqueueSnackbar]);

  /**
   * Determines the features that the item being dragged can snap to.
   */
  const snapGetFeatures = useCallback((m: mapboxgl.Map) => {
    const f = [LayerIds.ugPoints, LayerIds.demand].map((layerId) => m.querySourceFeatures(sourceId, { sourceLayer: layerId })).flat();
    return f;
  }, []);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        snap: true,
        snapOptions: {
          snapPx: 25,
          snapToMidPoints: false,
          snapVertexPriorityDistance: 0.0025,
          snapGetFeatures,
        },
        modeOptions: {
          demands: { sourceId, sourceLayerId: LayerIds.demand },
          ugPoints: { sourceId, sourceLayerId: LayerIds.ugPoints },
        },
      }) as MapboxDrawOptions,
    [snapGetFeatures]
  );

  const handleOnConfirm = async () => {
    setIsSaving(true);

    const fc = featureCollection(
      drawControl.current?.getAll().features.map((f) => ({
        ...f,
        // Strip out any properties added during feature drawing that we do not wish to save
        properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
      }))
    );

    const file = new File([JSON.stringify(fc)], "geojson.json", {
      type: "text/plain",
    });

    dispatch(
      addImport({
        versionId,
        layerKey: LayerIds.leadIns,
        displayName: "Lead-Ins",
        files: [file],
      })
    );
  };

  if (isLoadingLeadIns || isLoadingDemands || isLoadingUgPoints) {
    return <SkeletonLoadingGrid spacing={2} items={[{ height: 40 }, { height: 130 }, { height: 38 }, { height: 300 }]} />;
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Draw on map" />

      {isSaving ? (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Saving changes" subtext="Processing..." />
        </Paper>
      ) : (
        <Alert severity="info">
          <AlertTitle>Edit leadins</AlertTitle>
        </Alert>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={isReadOnly} // TODO - isDirty?
          loading={isSaving}
        >
          Finish
        </LoadingButton>
      </Box>

      <DrawHandler
        startingMode="simple_select"
        initialFeatures={initialFeatures}
        sources={sources}
        config={config}
        options={options}
        styles={leadInDrawStyles}
        modes={modes}
        // getFeatureProperties={}  // TODO: need to add layerId
      />

      {isSaving && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default LeadInDrawPanel;
